/**
 * TiltedPreviewStack — a layered, slightly-tilted stack of 3 (or more) preview
 * tiles. Used on every product page's right column. Tiles separate subtly on
 * hover (framer-motion) and have thin hairline borders + faint drop shadow.
 *
 * @example
 *   <TiltedPreviewStack
 *     items={[
 *       { image: "/path/a.jpg", label: "EDITORIAL TEMPLATE" },
 *       { image: "/path/b.jpg" },
 *       { render: <CustomMockTile /> },
 *     ]}
 *   />
 */
import React, { useState } from "react";
import { motion } from "framer-motion";

const TILT_PRESETS = [
  // first = front-most, last = back-most.
  // Tile 2/3 are pushed further out so all three are visibly distinct at rest
  // (previous values made them stack almost flush, reading as "one card").
  { rotate: -3.0, x:   0, y:   0, scale: 1.00, z: 30 },
  { rotate:  4.5, x: 110, y:  60, scale: 0.92, z: 20 },
  { rotate: -2.5, x: -80, y: 130, scale: 0.86, z: 10 },
  { rotate:  6.0, x:  20, y: 130, scale: 0.82, z:  5 },
];

/* Hover separation: each tile drifts outward further, like a hand of cards
   spreading. Front tile barely moves so the headline anchor stays put. */
const HOVER_NUDGE = [
  { x:   0, y:   0, rotate:  0 },
  { x:  60, y: -20, rotate:  3 },
  { x: -60, y:  20, rotate: -3 },
  { x:  16, y:  32, rotate:  3 },
];

function Tile({ item, idx, isHovered }) {
  const tilt = TILT_PRESETS[idx] || TILT_PRESETS[TILT_PRESETS.length - 1];
  const nudge = HOVER_NUDGE[idx] || HOVER_NUDGE[HOVER_NUDGE.length - 1];
  const target = isHovered
    ? {
        rotate: tilt.rotate + nudge.rotate,
        x: tilt.x + nudge.x,
        y: tilt.y + nudge.y,
        scale: tilt.scale,
      }
    : { rotate: tilt.rotate, x: tilt.x, y: tilt.y, scale: tilt.scale };

  return (
    <motion.div
      className="absolute inset-0 origin-center"
      style={{ zIndex: tilt.z }}
      initial={false}
      animate={target}
      transition={{ type: "spring", stiffness: 120, damping: 22, mass: 0.6 }}
    >
      <div
        className="relative w-full h-full overflow-hidden bg-[color:var(--charcoal-soft)]"
        style={{
          border: "1px solid rgba(232, 215, 168, 0.20)",
          boxShadow:
            "0 30px 60px -30px rgba(0,0,0,0.7), 0 12px 24px -16px rgba(0,0,0,0.5)",
        }}
      >
        {item.render ? (
          item.render
        ) : item.image ? (
          <img
            src={item.image}
            alt={item.alt || ""}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        {item.label ? (
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
            <span className="ed-eyebrow px-2 py-1 bg-[color:var(--charcoal)]/70 backdrop-blur-sm text-[color:var(--cream)] border border-[color:var(--gold-foil)]/30">
              {item.label}
            </span>
            {item.icon ? <span className="text-[color:var(--gold-foil)]">{item.icon}</span> : null}
          </div>
        ) : null}
      </div>
    </motion.div>
  );
}

export default function TiltedPreviewStack({
  items = [],
  aspect = "3 / 4",
  className = "",
  ...rest
}) {
  const [hover, setHover] = useState(false);
  // Render back-to-front so DOM order matches z-stacking.
  const ordered = [...items].slice(0, 4).reverse();
  return (
    <div
      className={`relative w-full ${className}`}
      style={{ aspectRatio: aspect }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      {ordered.map((item, i) => {
        // i in `ordered` is back-to-front; idx prop wants front-to-back.
        const idx = ordered.length - 1 - i;
        return <Tile key={idx} item={item} idx={idx} isHovered={hover} />;
      })}
    </div>
  );
}
